
import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

type UtilityType = 'energy' | 'gas' | 'water';

interface UsageAreaChartProps {
  data: any[];
  hiddenSeries: string[];
  utilityType: UtilityType;
}

const UsageAreaChart: React.FC<UsageAreaChartProps> = ({ data, hiddenSeries, utilityType }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-400">No data available</p>
      </div>
    );
  }

  const getGradientId = () => {
    switch (utilityType) {
      case 'energy':
        return 'colorElectricity';
      case 'gas':
        return 'colorGas';
      case 'water':
        return 'colorWater';
    }
  };

  const getColor = () => {
    switch (utilityType) {
      case 'energy':
        return '#30D158';
      case 'gas':
        return '#FF453A';
      case 'water':
        return '#0EA5E9';
    }
  };

  const getDataKey = () => {
    switch (utilityType) {
      case 'energy':
        return 'electricity';
      case 'gas':
        return 'gas';
      case 'water':
        return 'water';
    }
  };

  const getUnit = () => {
    switch (utilityType) {
      case 'energy':
        return 'kWh';
      case 'gas':
        return 'm³';
      case 'water':
        return 'm³';
    }
  };
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          top: 5,
          right: 5,
          left: 0,
          bottom: 5,
        }}
      >
        <defs>
          <linearGradient id={getGradientId()} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={getColor()} stopOpacity={0.9}/>
            <stop offset="95%" stopColor={getColor()} stopOpacity={0.6}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#444444" />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 12, fill: '#ffffff' }} 
          axisLine={{ stroke: '#444444' }}
          tickLine={false}
        />
        <YAxis 
          tick={{ fontSize: 12, fill: '#ffffff' }} 
          axisLine={{ stroke: '#444444' }}
          tickLine={false}
          width={40}
        />
        <Tooltip 
          formatter={(value) => [`${value} ${getUnit()}`, utilityType.charAt(0).toUpperCase() + utilityType.slice(1)]}
          contentStyle={{ 
            borderRadius: '8px', 
            border: 'none', 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            backgroundColor: 'rgba(26, 31, 44, 0.9)',
            color: '#fff'
          }}
          labelStyle={{ color: '#fff' }}
          itemStyle={{ color: '#fff' }}
        />
        <Area 
          type="monotone" 
          dataKey={getDataKey()}
          name={`${utilityType.charAt(0).toUpperCase() + utilityType.slice(1)} (${getUnit()})`}
          stroke={getColor()}
          fillOpacity={1} 
          fill={`url(#${getGradientId()})`}
          hide={hiddenSeries.includes(getDataKey())}
          activeDot={{ r: 6, strokeWidth: 1 }}
          dot={{ r: 3, strokeWidth: 1, fill: "#30303C" }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default UsageAreaChart;
